import { useState, useEffect } from 'react';
import { Mail, Send, Check } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function buildConfirmationMessage(civ, nom) {
    return `Bonjour ${civ} ${nom},

À la suite de notre échange téléphonique, nous avons le plaisir de vous confirmer la planification d'un rendez-vous avec l'un de nos conseillers. Cette démarche est proposée à titre informatif et ne constitue aucun engagement de votre part.

Afin de vous permettre de mieux découvrir notre entreprise et la qualité de nos services, vous trouverez ci-joint une présentation succincte de notre société. Nous vous invitons également à consulter notre site internet : www.bigdemenagement.fr

Nous restons naturellement à votre entière disposition pour toute information complémentaire et vous prions d'agréer l'expression de nos salutations distinguées.

L'équipe Big Déménagement`;
}

export function ConfirmationRDVDialog({ open, onOpenChange, prospect, visite, onDone }) {
    const [civilite, setCivilite] = useState('M.');
    const [destinataire, setDestinataire] = useState('');
    const [sujet, setSujet] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open || !prospect) return;
        setCivilite('M.');
        setDestinataire(prospect.email || '');
        setSujet(`Confirmation RDV - Big Déménagement`);
        setMessage(buildConfirmationMessage('M.', prospect.client_nom || ''));
    }, [open, prospect]);

    const updateCivilite = (val) => {
        setCivilite(val);
        setMessage(buildConfirmationMessage(val, prospect?.client_nom || ''));
    };

    const handleSend = async () => {
        if (!destinataire) {
            toast.error('Adresse email destinataire manquante');
            return;
        }
        setSending(true);
        try {
            await axios.post(
                `${API}/api/visites/send-confirmation-email`,
                {
                    destinataire,
                    sujet,
                    message,
                    civilite,
                    type_visite: visite?.type_visite || '',
                    date_visite: visite?.date || '',
                    heure: visite?.heure || '',
                },
                { withCredentials: true }
            );
            toast.success('Email de confirmation envoyé avec la présentation en pièce jointe');
            onDone?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'envoi de l'email");
        } finally {
            setSending(false);
        }
    };

    const handleSkip = () => {
        onDone?.();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!sending) onOpenChange(o); }}>
            <DialogContent
                className="max-w-2xl max-h-[90dvh] overflow-y-auto"
                data-testid="confirmation-rdv-dialog"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-blue-50">
                            <Mail size={22} className="text-[#2e7cd6]" strokeWidth={2} />
                        </div>
                        <div className="flex-1">
                            <DialogTitle className="text-lg font-semibold text-slate-900">
                                Envoyer la confirmation de rendez-vous
                            </DialogTitle>
                            <DialogDescription className="mt-1 text-slate-500 text-sm">
                                La présentation de l'entreprise (PDF) sera automatiquement jointe à l'email.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="flex items-center gap-4">
                        <Label className="text-slate-600 w-40 shrink-0">Civilité</Label>
                        <Select value={civilite} onValueChange={updateCivilite}>
                            <SelectTrigger className="max-w-sm ml-auto" data-testid="civilite-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="M.">M.</SelectItem>
                                <SelectItem value="Mme">Mme</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex items-center gap-4">
                        <Label className="text-slate-600 w-40 shrink-0">Destinataire</Label>
                        <Input
                            type="email"
                            value={destinataire}
                            onChange={(e) => setDestinataire(e.target.value)}
                            placeholder="email@exemple.fr"
                            className="max-w-sm ml-auto"
                            data-testid="email-destinataire"
                        />
                    </div>

                    <div className="flex items-center gap-4">
                        <Label className="text-slate-600 w-40 shrink-0">Sujet</Label>
                        <Input
                            value={sujet}
                            onChange={(e) => setSujet(e.target.value)}
                            className="max-w-sm ml-auto"
                            data-testid="email-sujet"
                        />
                    </div>

                    <div>
                        <Label className="text-slate-600 text-sm mb-2 block">Message</Label>
                        <Textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={10}
                            className="text-sm"
                            data-testid="email-message"
                        />
                    </div>

                    <div className="flex items-center gap-2 text-xs text-slate-500 bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2">
                        <Check size={14} className="text-[#2e7cd6]" />
                        <span>Présentation entreprise (PDF) jointe automatiquement</span>
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        variant="outline"
                        onClick={handleSkip}
                        disabled={sending}
                        data-testid="skip-email-btn"
                    >
                        Ne pas envoyer
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={sending || !destinataire}
                        className="bg-[#2e7cd6] hover:bg-[#246cbf] text-white"
                        data-testid="send-email-btn"
                    >
                        <Send size={16} className="mr-2" />
                        {sending ? 'Envoi en cours...' : 'Envoyer la confirmation'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
